// src/systems/rng.ts
//
// Phase 3 — pure RNG primitives. No imports.
// Mulberry32 + FNV-1a 32-bit string hash. Deterministic given a string or
// numeric seed. Matches the reference implementations from CONTEXT.md verbatim.
//
// Phase 13 (RNG-04): Rng upgraded to a callable interface with .clone() and .state()
// so look-ahead / preview callers can snapshot RNG state without advancing the source.

/**
 * Phase 13 (RNG-04): Rng is a callable function with snapshot helpers.
 * `clone()` returns a NEW Rng with identical internal state — the clone's
 * first call equals what the source's next call WOULD return, but
 * cloning itself does NOT advance the source.
 * `state()` returns the current internal seed (for save/debug — Phase 16
 *   reworked Peek + canonical-seed re-anchor consume it).
 */
export interface Rng {
  (): number;
  clone(): Rng;
  state(): number;
}

export function hashSeed(input: string | number): number {
  if (typeof input === 'number') return input >>> 0;
  // FNV-1a 32-bit
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function mulberry32(seed: number): Rng {
  let s = seed >>> 0;
  const fn = (() => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }) as Rng;
  // fn.clone snapshots the CURRENT value of s (not the original seed),
  // so it captures the post-advance state at the moment clone() is called.
  fn.clone = () => mulberry32(s);
  fn.state = () => s;
  return fn;
}

export function createRng(seed: string | number): Rng {
  return mulberry32(hashSeed(seed));
}

// Helpers (used by wheel, deck, etc.)
export function pickIndex(rng: Rng, length: number): number {
  return Math.floor(rng() * length);
}

export function pickFromArray<T>(rng: Rng, arr: readonly T[]): T {
  return arr[pickIndex(rng, arr.length)];
}

export function shuffle<T>(rng: Rng, arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = pickIndex(rng, i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
